import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PdfDownloadIcon } from '~/assets/aiInsights';
import { PdfReport } from '~/components/AIInsightsModal/AIInsightsModal.utils';
import { useGenerateReportDownloadUrlMutation } from '~/types/api.graphql';

import { PdfReportButton, PdfReportError, PdfReportIconWrapper } from './AIInsightsUI.styles';

interface Props {
  report: PdfReport;
}

const PdfReportBlock = ({ report }: Props) => {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState(false);
  const [generateReportDownloadUrl, { loading }] = useGenerateReportDownloadUrlMutation();

  // The BE presigns with `Content-Disposition: attachment`, so the browser downloads the file
  // instead of navigating. A programmatic anchor click triggers the download without opening a tab
  // or leaving the page; the `download` attr is ignored cross-origin but the header takes precedence.
  const handleClick = async (): Promise<void> => {
    setHasError(false);
    try {
      const { data } = await generateReportDownloadUrl({
        variables: { relativePath: report.relativePath },
      });
      if (data?.generateReportDownloadUrl) {
        const link = document.createElement('a');
        link.href = data.generateReportDownloadUrl;
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        setHasError(true);
      }
    } catch {
      setHasError(true);
    }
  };

  return (
    <div>
      <PdfReportButton type="button" disabled={loading} onClick={() => void handleClick()}>
        <PdfReportIconWrapper>
          <PdfDownloadIcon />
        </PdfReportIconWrapper>
        {t('aiInsights.downloadPdfReport')}
      </PdfReportButton>
      {hasError && <PdfReportError>{t('aiInsights.downloadPdfReportError')}</PdfReportError>}
    </div>
  );
};

export { PdfReportBlock };
